'use client';

import { Box, FormHelperText, Typography, styled } from '@mui/material';
import { LDClient, withLDConsumer } from 'launchdarkly-react-client-sdk';
import { useCallback, useEffect, useRef, useState } from 'react';

import { sendPurchaseEvent } from '@nrdy-marketing-engine/tracking/src/experiment/checkout-experiment';
import { useRouter } from 'next/navigation';
import { useCartContext } from '../../cart-context';
import useCheckoutCompletionRouting from '../../hooks/use-checkout-completion-routing';
import useCheckoutUserMetrics from '../../hooks/use-checkout-user-metrics';
import useEnterToSubmit from '../../hooks/use-enter-to-submit';
import useInsensitiveSearchParams from '../../hooks/use-insensitive-search-params';
import usePayment from '../../hooks/use-payment';
import useScrollToError from '../../hooks/use-scroll-to-error';
import Subtitle from '../shared/subtitle';
import Title from '../shared/title';
import AgreeToTerms from './agree-to-terms/agree-to-terms';
import CartItem from './cart-item/cart-item';
import CartTotal from './cart-total';
import ContactFields from './contact-fields';
import { getFailureReason } from './helpers';
import PaymentButton from './payment/payment-button';
import PromoCode from './promo-code';

const ErrorText = styled(FormHelperText)({
  marginBottom: '1rem',
  marginTop: '1rem',
});

const ErrorLink = styled('a')({
  '&:focus, &:visited, &:link, &:active': {
    textDecoration: 'none',
  },
});

interface DetailsStepProps {
  ldClient?: LDClient;
}

function DetailsStep({ ldClient }: DetailsStepProps) {
  const { enableRoutingEffects } = useCheckoutCompletionRouting({ ldClient });
  const [clickedBraintreeCard, setClickedBraintreeCard] = useState<boolean>(false);
  const { useFormVals, cartState, applyPromoCode, removePromoCode } = useCartContext();
  const [paymentErrorMessage, setPaymentErrorMessage] = useState<string>('');
  const { failed, paymentRequestable, paymentType, submitPayment, loading } = usePayment();
  const { control, handleSubmit, formState } = useFormVals;
  const router = useRouter();
  const searchParams = useInsensitiveSearchParams();
  const enrollmentItemId = searchParams.get('enrollmentItemId');
  const braintreeCardElement = document.getElementsByClassName('braintree-option__card').item(0);

  const showPromoCode = ldClient?.variation('DISCO-342-show-promo-code-nme-checkout', false);
  const quoteId = searchParams.get('q');
  useCheckoutUserMetrics();

  const referrer = typeof document !== 'undefined' ? document.referrer : '';
  const shouldShowPromoCode = showPromoCode && !referrer.includes('quote');

  useEffect(
    function _autoClickCardOption() {
      if (!braintreeCardElement || clickedBraintreeCard) return;

      setClickedBraintreeCard(true);
      braintreeCardElement.dispatchEvent(
        new MouseEvent('click', {
          bubbles: true,
          buttons: 1,
          cancelable: true,
          view: window,
        })
      );
    },
    [braintreeCardElement, clickedBraintreeCard]
  );

  const redirect = useCallback(async () => {
    await sendPurchaseEvent('success');
    if (enrollmentItemId) {
      router.replace(`/membership/enroll/${enrollmentItemId}?thankyou=1`);
    } else {
      enableRoutingEffects();
    }
  }, [enrollmentItemId, router, enableRoutingEffects]);

  const onSubmit = useCallback(async () => {
    if (loading || !paymentRequestable) return;

    await sendPurchaseEvent('started');

    submitPayment()
      .then(redirect)
      .catch((err) => {
        console.error(err);
        setPaymentErrorMessage(getFailureReason(err));
        sendPurchaseEvent('error');
      });
  }, [redirect, submitPayment, loading, paymentRequestable]);

  const boxRef = useRef<HTMLDivElement>(null);
  const onInvalid = useScrollToError(boxRef);

  useEnterToSubmit(handleSubmit(onSubmit, onInvalid));

  return (
    <>
      <Box marginBottom={3} ref={boxRef}>
        <Title variant="h1">Membership details and payment</Title>
        <Subtitle>Account info</Subtitle>
        <Typography fontSize={'12px'} marginBottom={1}>
          We will use this number to contact you and connect you with a tutor.
        </Typography>
        <ContactFields />
        {shouldShowPromoCode && !quoteId ? (
          <PromoCode cartState={cartState} onApply={applyPromoCode} onRemove={removePromoCode} />
        ) : null}
        <CartItem />
        {failed && (
          <ErrorText error>
            There is a problem with our payment system. Please call our support team at{' '}
            <ErrorLink href="tel:8008034058">800-803-4058</ErrorLink> to complete your purchase.
          </ErrorText>
        )}
        {!failed && (
          <>
            <Box id="braintree-container" marginBottom={2} marginTop={3} width="100%" />
            {paymentRequestable && <CartTotal />}
            {paymentRequestable && (
              <AgreeToTerms
                contracts={cartState.contracts}
                control={control}
                errorMessage={formState.errors?.agreeToTerms?.message}
              />
            )}
            {paymentErrorMessage?.length > 0 && (
              <ErrorText error>
                {paymentErrorMessage} call our support team at{' '}
                <ErrorLink href="tel:8008034058">800-803-4058</ErrorLink> to complete your purchase.
              </ErrorText>
            )}
          </>
        )}
      </Box>

      {!failed && (
        <PaymentButton
          loading={loading}
          onClick={handleSubmit(onSubmit, onInvalid)}
          paymentRequestable={paymentRequestable}
          paymentType={paymentType}
        />
      )}
    </>
  );
}

export default withLDConsumer()(DetailsStep);
